import { useState } from 'react';
import { ExternalLink, Github } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { toast } from 'sonner';
import { githubApi, type GitHubAppView } from './api';

/**
 * Resolves the absolute install URL for a registered App. Prefers the
 * `htmlUrl` we recorded when we first fetched the App from GitHub, but
 * falls back to constructing one from the slug — important for older
 * env-imported rows that pre-date the JWT-backfill in `importFromEnv`
 * and therefore have a placeholder slug + null `htmlUrl`.
 *
 * The returned URL has NO `state` parameter set — the active org id
 * and the CSRF nonce are stamped on by the server's
 * `/github/app/install/begin` endpoint when the user actually clicks
 * "Install". See {@link InstallGitHubAppButton} for the click handler.
 */
export function resolveBaseInstallUrl(app: GitHubAppView): string | null {
  return app.htmlUrl
    ? `${app.htmlUrl}/installations/new`
    : app.slug
      ? `https://github.com/apps/${app.slug}/installations/new`
      : null;
}

/**
 * The canonical "Install on a GitHub Organization" affordance. Extracted from
 * the GitHub setup page so every surface that needs to push a user to install
 * the App — the setup page, the flow-publish gate, the blueprint repo picker —
 * shares one implementation of the CSRF-bound install flow.
 *
 * Audit H12: route every install click through `install/begin` so the server
 * can mint a one-shot CSRF nonce and stamp it onto GitHub's `state` parameter
 * alongside the active org id. The handler keeps the same visual surface
 * (anchor + new tab) and falls back to opening the un-stamped URL if the begin
 * endpoint fails — without `state`, the callback's membership cross-check still
 * binds the install to the caller's active org, just without the CSRF binding.
 */
export function InstallGitHubAppButton({
  app,
  organizationId,
  variant,
  onInstallStarted,
}: {
  app: GitHubAppView;
  organizationId: string | null;
  variant: 'primary' | 'link';
  onInstallStarted?: () => void;
}) {
  const baseHref = resolveBaseInstallUrl(app);
  const [pending, setPending] = useState(false);
  if (!baseHref) return null;

  async function onClick(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    if (pending || !baseHref) return;
    setPending(true);
    try {
      let target = baseHref;
      if (organizationId) {
        try {
          const { installUrl } = await githubApi.beginGitHubAppInstall(baseHref);
          target = installUrl;
        } catch (err) {
          // The CSRF mint is best-effort: the callback's existing
          // membership cross-check still scopes the install to the
          // caller's active org, so a transient API hiccup here
          // shouldn't block the user. Toast at warn-level so we still
          // notice when the begin endpoint is broken in the wild.
          toast.error(`Couldn't prepare install link: ${toErrorMessage(err)}`);
          return;
        }
      }
      // The install completes in a new tab, so the originating page has no
      // other signal it finished. Flip into the "waiting for GitHub" state;
      // the server's real-time `installation_synced` push resolves it.
      window.open(target, '_blank', 'noopener,noreferrer');
      onInstallStarted?.();
    } finally {
      setPending(false);
    }
  }

  if (variant === 'link') {
    return (
      <a
        href={baseHref}
        onClick={onClick}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        aria-disabled={pending}
        data-tutorial-id="github-install-org"
      >
        Add another installation <ExternalLink className="size-3" />
      </a>
    );
  }
  return (
    <Button asChild>
      <a href={baseHref} onClick={onClick} data-tutorial-id="github-install-org">
        <Github className="size-4" />
        Install on a GitHub Organization
      </a>
    </Button>
  );
}
